import { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import CommandPalette from './components/layout/CommandPalette';
import { ALL_TOOLS, findTool, TOOL_GROUPS } from './tools';

const STORAGE_KEY = 'cpi-toolkit:active-tool';
const SIDEBAR_STORAGE_KEY = 'cpi-toolkit:sidebar-collapsed';
const RECENTS_STORAGE_KEY = 'cpi-toolkit:recent-tools';

function loadRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id) => ALL_TOOLS.some((t) => t.id === id));
  } catch {
    return [];
  }
}

export default function App() {
  const [activeId, setActiveId] = useState<string>(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && ALL_TOOLS.some((t) => t.id === stored)) return stored;
    return 'text-diff';
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  });
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [recentIds, setRecentIds] = useState<string[]>(loadRecents);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, activeId);
  }, [activeId]);

  useEffect(() => {
    window.localStorage.setItem(
      SIDEBAR_STORAGE_KEY,
      String(isSidebarCollapsed)
    );
  }, [isSidebarCollapsed]);

  const selectTool = useCallback((id: string) => {
    setActiveId(id);
    setIsMobileNavOpen(false);
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((v) => v !== id)].slice(0, 6);
      window.localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Global shortcut: Ctrl/Cmd+K opens the command palette
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const activeTool = useMemo(() => findTool(activeId)!, [activeId]);
  const activeGroup = useMemo(
    () => TOOL_GROUPS.find((g) => g.tools.some((t) => t.id === activeId)),
    [activeId]
  );
  const ActiveComponent = activeTool.component;

  return (
    <div className="min-h-screen flex">
      <div className="app-grid" aria-hidden="true" />
      {isMobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-950/60 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}
      <Sidebar
        groups={TOOL_GROUPS}
        activeId={activeId}
        onSelect={selectTool}
        isCollapsed={isSidebarCollapsed}
        isMobileOpen={isMobileNavOpen}
      />
      <div className="relative z-10 flex-1 min-w-0 flex flex-col">
        <Header
          tool={activeTool}
          groupLabel={activeGroup?.label}
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={() => setIsSidebarCollapsed((value) => !value)}
          onOpenMobileNav={() => setIsMobileNavOpen(true)}
          onOpenPalette={() => setIsPaletteOpen(true)}
        />
        <main
          key={activeId}
          className="flex-1 overflow-auto p-4 lg:p-6 animate-fade-in"
        >
          <ActiveComponent />
        </main>
      </div>
      <CommandPalette
        open={isPaletteOpen}
        onClose={() => setIsPaletteOpen(false)}
        onSelect={selectTool}
        recentIds={recentIds}
      />
    </div>
  );
}
